import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import Header from '@/components/layout/Header';
import CorporateFooter from '@/components/layout/CorporateFooter';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Share2,
  Bookmark,
  Heart,
  Newspaper,
} from 'lucide-react';
import { NewsItem } from '@/types';
import { supabase } from '@/integrations/supabase/client';
import { uniqueTopic } from '@/lib/realtime';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/common/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { formatContentForDisplay } from '@/utils/htmlUtils';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { buildNewsArticleSchema, stripHtml, toSlug, truncateText } from '@/lib/seo';
import { trackPageView } from '@/lib/ga';

const formatDate = (dateString: string) => {

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const shareArticle = () => {
  if (navigator.share) {
    navigator.share({
      title: 'Share this article',
      url: window.location.href,
    });
  } else {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  }
};

const NewsDetail = () => {
  const { newsId } = useParams<{ newsId: string }>();
  const navigate = useNavigate();
  const [newsItem, setNewsItem] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatedNews, setRelatedNews] = useState<NewsItem[]>([]);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState<number>(0);
  const [likeLoading, setLikeLoading] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [newsId]);

  useEffect(() => {
    let isMounted = true;

    async function fetchNewsItem() {
      if (!newsId) {
        setNewsItem(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setNewsItem(null);

      const { data, error } = await (supabase as any)
        .from('news')
        .select('*')
        .eq('id', newsId)
        .single();

      if (!isMounted) return;

      if (error) {
        setNewsItem(null);
        toast({
          title: 'Error loading article',
          description: error.message,
          variant: 'destructive',
        });
      } else if (!data) {
        setNewsItem(null);
      } else {
        setNewsItem(data);
        setLikeCount(data.likes || 0);
      }

      setLoading(false);
    }

    void fetchNewsItem();
    return () => {
      isMounted = false;
    };
  }, [newsId, toast]);

  useEffect(() => {
    let isMounted = true;

    async function fetchRelatedNews() {
      if (!newsId) {
        setRelatedNews([]);
        return;
      }

      const { data, error } = await supabase
        .from('news')
        .select('*')
        .neq('id', newsId)
        .order('date', { ascending: false })
        .limit(3);

      if (!isMounted) return;

      if (!error && Array.isArray(data)) {
        setRelatedNews(data);
      } else {
        console.error('Error fetching related news:', error);
        setRelatedNews([]);
      }
    }

    void fetchRelatedNews();
    return () => {
      isMounted = false;
    };
  }, [newsId]);


  useEffect(() => {
    const fetchLikeStatus = async () => {
      if (!user || !newsId) {
        setIsLiked(false);
        return;
      }

      const { data } = await (supabase as any)
        .from('news_likes')
        .select('id')
        .eq('news_id', newsId)
        .eq('user_id', user.id)
        .maybeSingle();

      setIsLiked(!!data);
    };

    void fetchLikeStatus();
  }, [user, newsId]);

  useEffect(() => {
    if (!newsId) return;

    const channel = supabase
      .channel(uniqueTopic('realtime-news-likes'))
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'news_likes', filter: `news_id=eq.${newsId}` },
        () => {
          void (supabase as any)
            .from('news')
            .select('likes')
            .eq('id', newsId)
            .single()
            .then(({ data }: { data: { likes?: number } | null }) => {
              setLikeCount(data?.likes || 0);
            });

        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [newsId]);

  useEffect(() => {
    if (!newsItem) return;

    trackPageView(`/news/${newsItem.id}/${toSlug(newsItem.title)}`, `${newsItem.title} | CanXP AI News`);
  }, [newsItem]);

  const handleLike = async () => {

    if (!user) {
      toast({
        title: 'Login required',
        description: 'You must be logged in to like this article.',
        variant: 'destructive',
      });
      return;
    }

    if (!newsId) return;

    setLikeLoading(true);
    try {
      if (!isLiked) {
        const { error: likeError } = await (supabase as any)
          .from('news_likes')
          .insert({ news_id: newsId, user_id: user.id });

        if (likeError && !likeError.message.includes('duplicate')) throw likeError;

        await (supabase as any).rpc('increment_news_likes', { news_id_input: newsId });

        setIsLiked(true);
        setLikeCount((prev) => prev + 1);
      } else {
        await (supabase as any)
          .from('news_likes')
          .delete()
          .eq('news_id', newsId)
          .eq('user_id', user.id);

        await (supabase as any).rpc('decrement_news_likes', { news_id_input: newsId });

        setIsLiked(false);
        setLikeCount((prev) => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error updating like status:', err);
      toast({
        title: 'Error',
        description: 'Could not update like status.',
        variant: 'destructive',
      });
    } finally {
      setLikeLoading(false);
    }
  };


  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background text-foreground">
          <Header />
          <div className="container mx-auto flex max-w-5xl items-center justify-center px-4 py-16">
            <div className="flex w-full max-w-3xl animate-pulse flex-col space-y-4">
              <div className="h-6 w-3/4 rounded bg-muted/50" />
              <div className="h-80 rounded bg-muted/30" />
              <div className="space-y-2">
                <div className="h-4 w-5/6 rounded bg-muted/50" />
                <div className="h-4 w-full rounded bg-muted/50" />
                <div className="h-4 w-full rounded bg-muted/50" />
                <div className="h-4 w-5/6 rounded bg-muted/50" />
                <div className="h-4 w-3/4 rounded bg-muted/50" />
              </div>
            </div>
          </div>
          <CorporateFooter />
        </div>
      </MainLayout>
    );
  }

  if (!newsItem) {
    return (
      <MainLayout>
        <SEO
          title="Article Not Found"
          description="The news article you are looking for could not be found."
        />
        <div className="min-h-screen bg-background text-foreground">
          <Header />
          <div className="container mx-auto max-w-3xl px-4 py-20 text-center">
            <Newspaper className="mx-auto h-16 w-16 text-muted-foreground opacity-30" />
            <h1 className="mt-4 text-2xl font-bold">Article Not Found</h1>
            <p className="mt-2 text-muted-foreground">Sorry, we couldn't find the article you're looking for.</p>
            <Button className="mt-8" onClick={() => navigate('/news')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to News
            </Button>
          </div>
          <CorporateFooter />
        </div>
      </MainLayout>
    );
  }

  const category = newsItem.category || 'General';
  const formattedDate = formatDate(newsItem.date);
  const formattedTime = formatTime(newsItem.date);
  const newsPath = `/news/${newsItem.id}/${toSlug(newsItem.title)}`;
  const newsDescription = truncateText(stripHtml(newsItem.content), 160);

  return (
    <MainLayout>
      <SEO
        title={`${newsItem.title} | CanXP AI News`}
        description={newsDescription}
        imageUrl={newsItem.image_url || undefined}
        url={newsPath}
        ogType="article"
        schema={buildNewsArticleSchema({
          title: newsItem.title,
          description: newsDescription,
          imageUrl: newsItem.image_url || undefined,
          publishedAt: newsItem.date,
          authorName: newsItem.author,
          path: newsPath,
        })}
      />

      <div className="min-h-screen bg-background text-foreground">
        <Header />

        <div className="relative">
          {newsItem.image_url ? (
            <div className="relative h-[50vh] min-h-[400px] w-full bg-gray-900">
              <div
                className="absolute inset-0 z-0 bg-cover bg-center opacity-60"
                style={{ backgroundImage: `url(${newsItem.image_url})` }}
              />
              <div className="absolute inset-0 z-10 bg-gradient-to-t from-background to-transparent" />
            </div>
          ) : null}

          <div className="relative z-20 container mx-auto max-w-5xl px-4 sm:px-6">
            <div className={`${newsItem.image_url ? '-mt-48' : 'pt-16'} pb-16`}>
              <div className="mb-8 flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 border-muted bg-background/80 backdrop-blur-sm hover:bg-background/90 hover:text-black dark:text-white"
                  onClick={() => navigate('/news')}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to News
                </Button>
                <Badge className="border-none bg-canada-red px-3 py-1.5 text-xs font-medium text-white hover:bg-canada-red/90">
                  {category}
                </Badge>
              </div>

              <h1 className="mb-6 text-4xl font-bold tracking-tight sm:text-5xl">{newsItem.title}</h1>

              <div className="mb-10 flex flex-wrap items-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <User size={14} />
                  <span>{newsItem.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={14} />
                  <span>{formattedDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} />
                  <span>{formattedTime}</span>
                </div>
              </div>

              <div className="mb-10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant={isLiked ? 'default' : 'outline'}
                    size="sm"
                    className={`rounded-full transition-colors ${isLiked ? 'bg-canada-red text-white hover:bg-canada-red/90' : ''}`}
                    onClick={handleLike}
                    disabled={likeLoading}
                  >
                    <Heart className={`mr-2 h-4 w-4 ${isLiked ? 'fill-white text-white' : ''}`} />
                    {isLiked ? 'Liked' : 'Like'}
                    <span className="ml-2">{likeCount}</span>
                  </Button>
                  <Button
                    variant={isBookmarked ? 'default' : 'outline'}
                    size="sm"
                    className={`rounded-full transition-colors ${isBookmarked ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}
                    onClick={() => setIsBookmarked(!isBookmarked)}
                  >
                    <Bookmark className={`mr-2 h-4 w-4 ${isBookmarked ? 'fill-current' : ''}`} />
                    Save
                  </Button>
                </div>
                <Button variant="outline" size="sm" className="rounded-full" onClick={shareArticle}>
                  <Share2 className="mr-2 h-4 w-4" />
                  Share
                </Button>
              </div>

              {newsItem.image_url && !newsItem.is_main_featured_article ? (
                <div className="mb-10 overflow-hidden rounded-lg shadow-lg">
                  <img src={newsItem.image_url} alt={newsItem.title} className="h-auto w-full" />
                </div>
              ) : null}

              <div className="prose prose-lg rich-text-content dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                  {newsItem.content}
                </ReactMarkdown>
              </div>

              <div className="mt-16 rounded-lg border bg-card/50 p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{newsItem.author}</h3>
                    <p className="text-sm text-muted-foreground">Admin</p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {relatedNews.length > 0 ? (
            <div className="border-t bg-muted/10 py-16">
              <div className="container mx-auto max-w-6xl px-4">
                <h2 className="mb-8 text-2xl font-bold">Related Articles</h2>
                <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                  {relatedNews.map((news) => (
                    <Card
                      key={news.id}
                      className="cursor-pointer overflow-hidden border-0 shadow transition-shadow hover:shadow-md"
                      onClick={() => navigate(`/news/${news.id}/${toSlug(news.title)}`)}
                    >

                      {news.image_url ? (
                        <div className="relative">
                          <Badge className="absolute left-3 top-3 z-10">{news.category || 'General'}</Badge>
                          <img src={news.image_url} alt={news.title} className="aspect-video w-full object-cover" />
                        </div>
                      ) : null}
                      <CardContent className="p-4">
                        <h3 className="mb-2 line-clamp-2 text-lg font-bold transition-colors hover:text-primary">{news.title}</h3>
                        <div className="line-clamp-2 text-sm text-muted-foreground">
                          <div dangerouslySetInnerHTML={{ __html: formatContentForDisplay(news.content.split('\n')[0]) }} />
                        </div>
                        <div className="mt-3 flex items-center justify-between border-t pt-3 text-xs text-muted-foreground">
                          <span>{formatDate(news.date)}</span>
                          <span>{news.author.split(',')[0]}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <CorporateFooter />
      </div>
    </MainLayout>
  );
};

export default NewsDetail;
